/**
 * THE EXPERT'S FACTS (EXPERT-NETWORKS.md §7b, E5b) — in an Expert Network's own room: the exact values its experts
 * use instead of guessing (its declared kinds), what agents proposed (approve / reject), add or change one.
 * Never a secret — the server refuses one and says why. Additive section.
 */
import React, { useState } from 'react';

const btn = 'px-2.5 py-1 text-xs font-medium rounded-md transition-colors';

function KindEditor({ kind, spec, onSave, busy }) {
  const fields = Object.keys(spec.fields || {});
  const [rec, setRec] = useState({});
  return (
    <div className="mt-1 grid gap-1" data-testid={`fact-editor-${kind}`}>
      {fields.map((f) => (
        <input key={f} value={rec[f] || ''} placeholder={`${f}${f === spec.key ? ' (required)' : ''} — ${spec.fields[f]}`}
          aria-label={f} onChange={(e) => setRec({ ...rec, [f]: e.target.value })}
          className="rounded border border-gray-300 px-2 py-1 text-[11px]" />
      ))}
      <div>
        <button type="button" disabled={busy || !(rec[spec.key] || '').trim()} data-testid={`fact-save-${kind}`}
          className={`${btn} bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50`}
          onClick={async () => { if (await onSave(kind, rec)) setRec({}); }}>
          Save fact
        </button>
      </div>
    </div>
  );
}

export default function RoomFacts({ value, onChange, intro = '', testId = 'room-facts' }) {
  const v = value || {};
  const kinds = v.kinds || {};
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [adding, setAdding] = useState('');

  const send = async (req) => {
    if (!onChange) return false;
    setBusy(true);
    setErr('');
    try {
      await onChange(req);
      return true;
    } catch (e) {
      setErr((e && e.message) || "Couldn't save that.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2 text-[12px]" data-testid={testId}>
      <p className="text-[11px] text-gray-500">
        {intro || "Exact values this Expert Network's experts use instead of guessing. Agents can only propose one; a person approves."}
      </p>
      {(v.proposals || []).length ? (
        <div className="rounded-md border border-amber-200 bg-amber-50/60 px-2 py-1.5" data-testid="fact-proposals">
          <p className="text-[11px] font-medium text-amber-900">Proposed by agents</p>
          {(v.proposals || []).map((p) => (
            <div key={p.proposalId} className="mt-1 flex flex-wrap items-center gap-2 text-[11px]" data-testid={`fact-proposal-${p.proposalId}`}>
              <span className="font-medium">{p.kind}</span>
              <span className="text-gray-700">{Object.entries(p.record || {}).map(([k, val]) => `${k}=${val}`).join('; ')}</span>
              {p.reason ? <span className="text-gray-500">— {p.reason}</span> : null}
              {v.canEdit ? (
                <>
                  <button type="button" disabled={busy} className={`${btn} bg-amber-600 text-white hover:bg-amber-700`}
                    onClick={() => send({ action: 'approve', proposalId: p.proposalId })}>Approve</button>
                  <button type="button" disabled={busy} className={`${btn} text-amber-800 hover:bg-amber-100`}
                    onClick={() => send({ action: 'reject', proposalId: p.proposalId })}>Reject</button>
                </>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {Object.entries(kinds).map(([kind, spec]) => {
        const recs = (v.records || {})[kind] || [];
        const fields = Object.keys(spec.fields || {});
        return (
          <div key={kind} data-testid={`fact-kind-${kind}`}>
            <div className="flex items-center gap-2">
              <span className="font-medium text-gray-800">{kind}</span>
              <span className="text-[10px] text-gray-400">{recs.length} held</span>
              {v.canEdit ? (
                <button type="button" className={`${btn} text-blue-700 hover:bg-blue-50`}
                  onClick={() => setAdding(adding === kind ? '' : kind)}>{adding === kind ? 'Close' : 'Add'}</button>
              ) : null}
            </div>
            {recs.length ? (
              <ul className="mt-0.5 space-y-0.5">
                {recs.map((r) => (
                  <li key={r[spec.key]} className="flex flex-wrap items-center gap-2 text-[11px] text-gray-700">
                    <span>{fields.filter((f) => r[f]).map((f) => `${f}=${r[f]}`).join('; ')}</span>
                    <span className="text-[10px] text-gray-400" title={r._at || ''}>{r._source}{r._by ? ` · ${r._by}` : ''}</span>
                    {v.canEdit ? (
                      <button type="button" disabled={busy} className={`${btn} text-rose-700 hover:bg-rose-50`}
                        onClick={() => send({ action: 'delete', kind, key: r[spec.key] })}>Remove</button>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : <p className="text-[11px] text-gray-400">None yet.</p>}
            {adding === kind ? (
              <KindEditor kind={kind} spec={spec} busy={busy}
                onSave={(k, rec) => send({ action: 'put', kind: k, record: rec })} />
            ) : null}
          </div>
        );
      })}
      {err ? <p className="text-[11px] text-rose-600" data-testid="facts-error">{err}</p> : null}
    </div>
  );
}
